<template>
  <AdminLayout>
    <template #content>
      <div class="purchase-edit-header d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h1 class="h4 mb-2">
            Modifier l'achat
          </h1>

          <div v-if="purchase" class="purchase-edit-meta d-flex flex-wrap align-items-center gap-2">
            <span class="badge" :class="statusClass(purchase.status)">
              {{ statusLabel(purchase.status) }}
            </span>
            <span class="purchase-edit-meta-item">
              <span class="text-muted">Date</span>
              <strong>{{ $format.date(purchase.purchase_date) }}</strong>
            </span>
            <span class="purchase-edit-meta-item">
              <span class="text-muted">Référence</span>
              <strong>{{ purchase.reference || '-' }}</strong>
            </span>
            <span class="purchase-edit-meta-item">
              <span class="text-muted">Fournisseur</span>
              <strong>{{ purchase.supplier_name || '-' }}</strong>
            </span>
          </div>
        </div>

        <div class="d-flex flex-wrap justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            @click="$router.push({ name: 'Purchases' })"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>

          <button
            v-if="purchase"
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            @click="$router.push({ name: 'PurchaseShow', params: { id: purchase.id } })"
          >
            <i class="fa fa-file-lines"></i>
            <span class="btn-label ms-1">Voir la fiche</span>
          </button>
        </div>
      </div>

      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement de l'achat...</div>
      </div>

      <div v-else-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>

      <div v-else-if="!purchase" class="alert alert-warning">
        Achat introuvable.
      </div>

      <div v-else>
          <PurchaseForm
            mode="edit"
            :purchase="purchase"
            @suspend="handlePurchaseSuspend"
            @finish="handlePurchaseFinish"
            @cancel="goToPurchaseShow"
          />
      </div>

      <SupplierPriceChangesDialog
        v-model:visible="supplierPriceChangesDialogVisible"
        :changes="supplierPriceChanges"
        :saving="isValidating"
        @confirm="confirmValidationWithSupplierPrices"
      />
    </template>
  </AdminLayout>
</template>

<script>
import PurchaseForm from './PurchaseForm.vue';
import SupplierPriceChangesDialog from './SupplierPriceChangesDialog.vue';

export default {
  name: 'PurchaseEditView',
  components: {
    PurchaseForm,
    SupplierPriceChangesDialog,
  },
  data() {
    return {
      purchase: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      supplierPriceChangesDialogVisible: false,
      supplierPriceChanges: [],
      isValidating: false,
    };
  },
  mounted() {
    this.fetchPurchase();
  },
  methods: {
    async fetchPurchase() {
      const id = this.$route.params.id;

      this.isLoading = true;
      this.errorMessage = '';
      this.purchase = null;

      try {
        const response = await this.$api.get(`/purchases/${id}`);
        this.purchase = response.data.data;
      } catch (error) {
        console.error('Erreur chargement achat', error);
        this.errorMessage = 'Impossible de charger cet achat.';
      } finally {
        this.isLoading = false;
      }
    },

    statusLabel(status) {
      return status === 'validated' ? 'Validé' : 'Brouillon';
    },

    statusClass(status) {
      return status === 'validated' ? 'bg-success' : 'bg-secondary';
    },

    goToPurchaseShow() {
      if (!this.purchase) {
        this.$router.push({ name: 'Purchases' });
        return;
      }

      this.$router.push({ name: 'PurchaseShow', params: { id: this.purchase.id } });
    },

    handlePurchaseSuspend(syncedPurchase) {
      if (syncedPurchase) {
        this.purchase = syncedPurchase;
      }

      this.goToPurchaseShow();
    },

    async handlePurchaseFinish(syncedPurchase) {
      if (syncedPurchase) {
        this.purchase = syncedPurchase;
      }

      await this.validatePurchase();
    },

    async validatePurchase() {
      if (!this.purchase || this.purchase.status !== 'draft') return;
      if (!this.purchase.items || !this.purchase.items.length) {
        this.errorMessage = 'Ajoutez au moins une ligne d’achat avant de valider.';
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await this.$api.get(
          `/purchases/${this.purchase.id}/supplier-price-changes`
        );
        const changes = response.data?.data?.changes || [];

        if (changes.length > 0) {
          this.supplierPriceChanges = changes;
          this.supplierPriceChangesDialogVisible = true;
          return;
        }
      } catch (error) {
        console.error('Erreur prévisualisation prix fournisseur', error);
        this.errorMessage = this.extractErrorMessage(
          error,
          'Impossible de vérifier les prix fournisseur avant validation.'
        );
        return;
      }

      const ok = await this.$confirmAction(
        `Valider l'achat "${this.purchase.reference}" ? Le stock sera mis à jour.`
      );
      if (!ok) return;

      await this.submitValidation(false, []);
    },

    async confirmValidationWithSupplierPrices(payload) {
      await this.submitValidation(
        payload.updateSupplierPrices,
        payload.productIds
      );
    },

    async submitValidation(updateSupplierPrices, productIds) {
      if (!this.purchase || this.purchase.status !== 'draft') return;

      this.isValidating = true;
      try {
        await this.$api.post(`/purchases/${this.purchase.id}/validate`, {
          update_supplier_prices: updateSupplierPrices,
          supplier_price_product_ids: productIds || [],
        });
        this.supplierPriceChangesDialogVisible = false;
        this.supplierPriceChanges = [];
        this.$router.push({ name: 'PurchaseShow', params: { id: this.purchase.id } });
      } catch (error) {
        console.error('Erreur validation achat', error);
        this.errorMessage = this.extractErrorMessage(
          error,
          "Une erreur est survenue lors de la validation de l'achat."
        );
      } finally {
        this.isValidating = false;
      }
    },

    extractErrorMessage(error, fallback) {
      if (error?.response?.data?.errors) {
        return Object.values(error.response.data.errors).flat().join(' ');
      }

      return fallback;
    },
  },
};
</script>

<style scoped>
.purchase-edit-header {
  min-height: 42px;
}

.purchase-edit-meta-item {
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  display: inline-flex;
  gap: 0.5rem;
  min-height: 28px;
  padding: 0.25rem 0.6rem;
}

@media (max-width: 767.98px) {
  .purchase-edit-header {
    flex-direction: column;
  }
}
</style>
